import { useMemo } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { MessageField } from '@components/MessageField';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { SOURCE_KEY } from '@layouts/pages/CRM/kanban-board/formatters/lead-source-label';
import { ELeadSource } from '@layouts/pages/CRM/kanban-board/services/responses/lead.response';
import { AutoReplyChannel } from '../services/automation.service';

/// Canais que podem ter saudação própria; Manual fica de fora porque cadastro à mão não abre conversa.
/// A lista espelha AutoReplyPicker.CanaisComResposta no servidor: mudar uma exige mudar a outra.
const CHANNELS: ELeadSource[] = [
    ELeadSource.WhatsApp,
    ELeadSource.Instagram,
    ELeadSource.Email,
    ELeadSource.Telegram,
    ELeadSource.Sms,
    ELeadSource.TikTok,
    ELeadSource.Website,
    ELeadSource.AdClick,
];

interface Props {
    value: AutoReplyChannel[];
    onChange: (next: AutoReplyChannel[]) => void;
    onBlur: () => void;
    fieldSx: object;
}

/// Saudações que fogem da geral; o canal entra só quando escolhido, para não virar parede de caixas.
export const AutoReplyChannels = ({ value, onChange, onBlur, fieldSx }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const used = useMemo(() => new Set(value.map((item) => item.source)), [value]);

    const available = useMemo(
        () => CHANNELS
            .filter((source) => !used.has(source))
            .map((source) => ({ value: String(source), label: t(SOURCE_KEY[source]) })),
        [used, t]
    );

    const add = (source: number) => onChange([...value, { source, message: '' }]);

    const edit = (source: number, message: string) =>
        onChange(value.map((item) => (item.source === source ? { ...item, message } : item)));

    const remove = (source: number) => {
        onChange(value.filter((item) => item.source !== source));
        onBlur();
    };

    return (
        <Box>
            <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: cc.textPrimary }}>
                {t('automacoes.mensagemPorCanal')}
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: cc.textMuted, mb: 1.5 }}>
                {t('automacoes.semCanalProprio')}
            </Typography>

            {value.length > 0 && (
                <Box sx={{ display: 'grid', gap: 1.25, mb: 1.25 }}>
                    {value.map((item) => (
                        <Box key={item.source} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <MessageField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    variableSize="large"
                                    label={t(SOURCE_KEY[item.source as ELeadSource])}
                                    value={item.message}
                                    onChange={(text) => edit(item.source, text)}
                                    onBlur={onBlur}
                                    inputProps={{ maxLength: 2000 }}
                                    sx={fieldSx}
                                />
                            </Box>

                            {/* Apagar o canal o devolve à mensagem geral; não desliga a resposta. */}
                            <Tooltip title={t('automacoes.voltarAoPadrao')}>
                                <IconButton
                                    size="small"
                                    onClick={() => remove(item.source)}
                                    sx={{ color: cc.textMuted, mt: 0.5 }}
                                >
                                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                </Box>
            )}

            {available.length > 0 && (
                <Box sx={{ width: 300 }}>
                    <StandaloneAutoCompleteSelect
                        options={available}
                        value={null}
                        onChange={(source) => source && add(Number(source))}
                        placeholder={t('automacoes.adicionarCanal')}
                    />
                </Box>
            )}
        </Box>
    );
};
